import { ListItemIcon, Menu, MenuItem, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useCloneWorkflow, useDeleteWorkflowById, useGetAllWorkflows, useTransferWorkflow } from "../api/workflowsApi";
import { useNavigate } from "react-router-dom";
import { WorkflowOutput } from "../types/workflow";
import { Delete, MoreVert, ContentCopy, SwapHoriz, Search, History, ViewModule, ViewList, LayersOutlined, CopyAll, AccountTree } from "@mui/icons-material";
import { useState, useMemo } from "react";
import { TableBodyRow, TableHeader, TableHeaderRow, TableData } from "./Common/TableElements";
import LoadingState from "./Common/LoadingState";
import { formatDateTime } from "../utils/misc";
import TransferWorkflowDialog from "./TransferWorkflowDialog";
import { toast } from "react-toastify";
import clsx from "clsx";

const Workflows = () => {
    const navigate = useNavigate();
    const { data: workflows, isLoading } = useGetAllWorkflows();
    const { mutate: deleteWorkflowById } = useDeleteWorkflowById();
    const { mutate: cloneWorkflow } = useCloneWorkflow();
    const { mutate: transferWorkflow } = useTransferWorkflow();
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
    const [menuOpenedForId, setMenuOpenedForId] = useState('');
    const [transferDialogOpen, setTransferDialogOpen] = useState(false);
    const [workflowToTransfer, setWorkflowToTransfer] = useState<WorkflowOutput | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [viewMode, setViewMode] = useState<'table' | 'tile'>('table');

    const filteredWorkflows = useMemo(() => {
        if (!workflows || !Array.isArray(workflows)) return [];
        if (!searchQuery.trim()) return workflows;
        
        const query = searchQuery.toLowerCase();
        return workflows.filter(workflow => 
            workflow.name?.toLowerCase().includes(query) ||
            workflow.description?.toLowerCase().includes(query) ||
            workflow.id?.toLowerCase().includes(query)
        );
    }, [workflows, searchQuery]);

    const openWorkflow = (workflow: WorkflowOutput) => {
        navigate(`/workflow/setup/${workflow.id}`);
    }

    const handleTransferClick = (workflow: WorkflowOutput) => {
        setMenuAnchorEl(null);
        setWorkflowToTransfer(workflow);
        setTransferDialogOpen(true);
        setMenuOpenedForId('');
    };

    const handleTransfer = (targetEmail: string) => {
        if (workflowToTransfer) {
            transferWorkflow({ 
                workflowId: workflowToTransfer.id, 
                targetEmail 
            });
        }
    };

    const handleCopyId = (workflowId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        navigator.clipboard.writeText(workflowId);
        toast.success('Workflow ID copied to clipboard');
    };

    return (
        <div className="bg-backgroundGrey w-full h-full flex flex-col overflow-hidden">
            {/* Header Section */}
            <div className="bg-white border-b border-borderGrey px-8 py-6">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-backgroundBlue rounded-lg">
                            <AccountTree className="text-nanonetsBlue" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-semibold text-greyishBlueMid mb-1">My Workflows</h1>
                            <p className="text-sm text-greyishBlue">
                                {workflows?.length || 0} {workflows?.length === 1 ? 'workflow' : 'workflows'} total
                            </p>
                        </div>
                    </div>
                    {/* View Toggle */}
                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={(_, newView) => {
                            if (newView !== null) {
                                setViewMode(newView);
                            }
                        }}
                        size="small"
                        sx={{
                            '& .MuiToggleButton-root': {
                                border: '1px solid #D6D8E0',
                                color: 'greyishBlue',
                                '&.Mui-selected': {
                                    backgroundColor: 'backgroundBlue',
                                    color: 'nanonetsBlue',
                                    borderColor: 'nanonetsBlue',
                                    '&:hover': {
                                        backgroundColor: 'backgroundBlue',
                                    },
                                },
                            },
                        }}
                    >
                        <ToggleButton value="table" aria-label="table view">
                            <ViewList className="text-base mr-1" />
                            <span className="text-xs">Table</span>
                        </ToggleButton>
                        <ToggleButton value="tile" aria-label="tile view">
                            <ViewModule className="text-base mr-1" />
                            <span className="text-xs">Tile</span>
                        </ToggleButton>
                    </ToggleButtonGroup>
                </div>
                
                {/* Search Bar */}
                <div className="relative max-w-md">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search className="h-5 w-5 text-greyishBlue" />
                    </div>
                    <input
                        type="text"
                        placeholder="Search workflows by name, description, or ID..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 border border-borderGrey rounded-lg bg-white 
                                   focus:outline-none focus:ring-2 focus:ring-nanonetsBlue focus:border-transparent
                                   text-sm text-greyishBlueMid placeholder:text-greyishBlueLight
                                   transition-all duration-200"
                    />
                </div>
            </div>

            {/* Content Section */}
            <div className="flex-1 overflow-auto bg-backgroundGrey px-8 py-6">
                {isLoading ? (
                    <div className="flex items-center justify-center h-full">
                    <LoadingState />
                    </div>
                ) : (
                    <>
                        {filteredWorkflows.length > 0 ? (
                            viewMode === 'table' ? (
                                /* Table View - Card Style */
                                <div className="space-y-3">
                                    {filteredWorkflows.map((workflow) => (
                                        <div
                                                key={workflow.id}
                                            className="bg-white rounded-lg border border-borderGrey shadow-sm 
                                                       hover:shadow-md hover:border-nanonetsBlue/30
                                                       transition-all duration-200 cursor-pointer
                                                       flex items-center justify-between p-4 group"
                                                onClick={() => openWorkflow(workflow)}
                                        >
                                            {/* Left Section - Icon and Content */}
                                            <div className="flex items-center gap-4 flex-1 min-w-0">
                                                <div className="p-2 bg-backgroundBlue rounded-lg flex-shrink-0">
                                                    <LayersOutlined className="text-nanonetsBlue text-lg" />
                                                </div>
                                                <div className="flex-1 min-w-0 grid grid-cols-12 gap-4 items-center">
                                                    <div className="col-span-4">
                                                        <div className="text-xs text-greyishBlue mb-1">ID</div>
                                                        <div className="flex items-center gap-2">
                                                            <div className="font-mono text-xs text-greyishBlueMid truncate">
                                                                {workflow.id}
                                                            </div>
                                                            <button
                                                                className="p-1 rounded hover:bg-backgroundBlue transition-colors flex-shrink-0"
                                                                onClick={(e) => handleCopyId(workflow.id, e)}
                                                                title="Copy workflow ID"
                                                            >
                                                                <CopyAll className="text-sm text-greyishBlue hover:text-nanonetsBlue" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div className="col-span-3">
                                                        <div className="text-xs text-greyishBlue mb-1">Name</div>
                                                        <div className="font-medium text-sm text-greyishBlueMid truncate group-hover:text-nanonetsBlue transition-colors">
                                                            {workflow.name || 'Untitled Workflow'}
                                                        </div>
                                                    </div>
                                                    <div className="col-span-2">
                                                        <div className="text-xs text-greyishBlue mb-1">Description</div>
                                                        <div className="text-xs text-greyishBlue truncate">
                                                            {workflow.description || 'No description'}
                                                        </div>
                                                    </div>
                                                    <div className="col-span-3">
                                                        <div className="text-xs text-greyishBlue mb-1">Updated at</div>
                                                        <div className="text-xs text-greyishBlueMid">
                                                            {formatDateTime(workflow.updated_time)}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Right Section - Actions */}
                                            <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                                                <button
                                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md
                                                               text-nanonetsBlue hover:bg-backgroundBlue
                                                               transition-colors text-xs font-medium"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            navigate(`/workflow/history/${workflow.id}`)
                                                        }}
                                                    >
                                                    <History className="text-sm" />
                                                    <span>History</span>
                                                </button>
                                                <div 
                                                    className="p-1.5 rounded-md hover:bg-backgroundGrey cursor-pointer transition-colors"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setMenuAnchorEl(e.currentTarget);
                                                        setMenuOpenedForId(workflow.id);
                                                    }}
                                                >
                                                    <MoreVert className="text-base text-lightSlateGrey" />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                /* Tile View */
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                    {filteredWorkflows.map((workflow) => (
                                        <div
                                            key={workflow.id}
                                            className="bg-white rounded-lg border border-borderGrey shadow-sm 
                                                       hover:shadow-md hover:border-nanonetsBlue/30
                                                       transition-all duration-200 cursor-pointer
                                                       flex flex-col group"
                                            onClick={() => openWorkflow(workflow)}
                                        >
                                            {/* Card Header */}
                                            <div className="p-5 pb-3 flex items-start justify-between">
                                                <div className="flex items-start gap-3 flex-1 min-w-0">
                                                    <div className="mt-1 p-2 bg-backgroundBlue rounded-lg flex-shrink-0">
                                                        <LayersOutlined className="text-nanonetsBlue text-lg" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <h3 className="font-semibold text-sm text-greyishBlueMid truncate mb-1 group-hover:text-nanonetsBlue transition-colors">
                                                            {workflow.name || 'Untitled Workflow'}
                                                        </h3>
                                                        <p className="text-xs text-greyishBlue line-clamp-2">
                                                            {workflow.description || 'No description'}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div
                                                    className="ml-2 p-1.5 rounded-md hover:bg-backgroundGrey 
                                                               transition-colors flex-shrink-0"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setMenuAnchorEl(e.currentTarget);
                                                            setMenuOpenedForId(workflow.id);
                                                        }}
                                                    >
                                                    <MoreVert className="text-lightSlateGrey text-lg" />
                                                </div>
                                            </div>

                                            {/* Card Footer */}
                                            <div className="px-5 py-3 border-t border-borderGrey bg-backgroundGrey/30 
                                                           flex items-center justify-between mt-auto">
                                                <div className="flex items-center gap-2 text-xs text-greyishBlue">
                                                    <span className="font-medium">ID:</span>
                                                    <span className="font-mono text-2xs">{workflow.id}</span>
                                                    <button
                                                        className="p-1 rounded hover:bg-backgroundBlue transition-colors ml-1"
                                                        onClick={(e) => handleCopyId(workflow.id, e)}
                                                        title="Copy workflow ID"
                                                    >
                                                        <CopyAll className="text-xs text-greyishBlue hover:text-nanonetsBlue" />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Updated Time Badge */}
                                            <div className="px-5 pb-3 pt-1">
                                                <div className="text-2xs text-greyishBlue">
                                                    Updated {formatDateTime(workflow.updated_time)}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )
                        ) : (
                            <div className="flex flex-col items-center justify-center h-full min-h-[400px]">
                                <h3 className="text-lg font-semibold text-greyishBlueMid mb-2">
                                    {searchQuery ? 'No workflows found' : 'No workflows yet'}
                                </h3>
                                <p className="text-sm text-greyishBlue text-center max-w-md">
                                    {searchQuery 
                                        ? `No workflows match "${searchQuery}". Try a different search term.`
                                        : 'Get started by creating your first workflow to automate your tasks.'}
                                </p>
                                                    </div>
                        )}
                    </>
                )}
                        </div>

            {/* Menu */}
                        <Menu
                            anchorEl={menuAnchorEl}
                            open={Boolean(menuAnchorEl)}
                            disableAutoFocusItem
                            onClose={() => setMenuAnchorEl(null)}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left',
                            }}
                PaperProps={{
                    className: "!shadow-lg !border !border-borderGrey !rounded-lg !mt-1"
                            }}
                        >
                            <MenuItem
                                onClick={() => {
                                    setMenuAnchorEl(null);
                                    if (menuOpenedForId) {
                                        cloneWorkflow({ workflowId: menuOpenedForId });
                                    }
                                    setMenuOpenedForId('');
                                }}
                    className="!text-xs !py-2 !px-3 hover:!bg-backgroundBlue"
                            >
                                <ListItemIcon className="mr-2 !min-w-0">
                        <ContentCopy className="!text-sm !text-greyishBlue" />
                                </ListItemIcon>
                                Duplicate workflow
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    const workflow = workflows?.find(w => w.id === menuOpenedForId);
                                    if (workflow) {
                                        handleTransferClick(workflow);
                                    }
                                }}
                    className="!text-xs !py-2 !px-3 hover:!bg-backgroundBlue"
                            >
                                <ListItemIcon className="mr-2 !min-w-0">
                        <SwapHoriz className="!text-sm !text-greyishBlue" />
                                </ListItemIcon>
                                Transfer workflow
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    setMenuAnchorEl(null);
                                    menuOpenedForId && deleteWorkflowById({workflowId: menuOpenedForId});
                                    setMenuOpenedForId('');
                                }}
                    className="!text-xs !py-2 !px-3 !text-[#C05353] hover:!bg-red-50"
                            >
                            <ListItemIcon className="mr-2 !min-w-0">
                                <Delete className="!text-sm" htmlColor="#C05353" />
                            </ListItemIcon>
                                Delete workflow
                            </MenuItem>
                        </Menu>

            {/* Transfer Dialog */}
                        <TransferWorkflowDialog
                            open={transferDialogOpen}
                            workflowId={workflowToTransfer?.id || ''}
                            workflowName={workflowToTransfer?.name || ''}
                            onClose={() => {
                                setTransferDialogOpen(false);
                                setWorkflowToTransfer(null);
                            }}
                            onTransfer={handleTransfer}
                        />
        </div>
    )
}

export default Workflows;